import { z } from "zod";
import { VALIDATION_RULES } from "@monorepo/config";

// ----------------------------------------------------------------------
// Constants & Messages
// ----------------------------------------------------------------------

export const SIGNIN_ERROR_MESSAGES = {
  REQUIRED: "This field is required",
  EMAIL_INVALID: "Please enter a valid email address",
  EMAIL_MAX: `Email must be less than ${VALIDATION_RULES.EMAIL_MAX_LENGTH} characters`,
  PASSWORD_REQUIRED: "Password is required",
  PASSWORD_MAX: "Password is too long",
  PASSWORD_MIN: `Password must be at least ${VALIDATION_RULES.PASSWORD_MIN_LENGTH} characters`,
  OTP_REQUIRED: "Verification code is required",
  OTP_FORMAT: `Code must be exactly ${VALIDATION_RULES.OTP_LENGTH} digits`,
  BACKUP_CODE_REQUIRED: "Backup code is required",
  BACKUP_CODE_MIN: `Backup code must be at least ${VALIDATION_RULES.BACKUP_CODE_MIN_LENGTH} characters`,
  PASSWORDS_MUST_MATCH: "Passwords do not match",
} as const;

// ----------------------------------------------------------------------
// Identify Step (Email)
// ----------------------------------------------------------------------

export const signinIdentifySchema = z.object({
  email: z
    .string()
    .trim()
    .toLowerCase()
    .min(1, SIGNIN_ERROR_MESSAGES.REQUIRED)
    .email(SIGNIN_ERROR_MESSAGES.EMAIL_INVALID)
    .max(VALIDATION_RULES.EMAIL_MAX_LENGTH, SIGNIN_ERROR_MESSAGES.EMAIL_MAX),
});

export type SigninIdentifyFormData = z.infer<typeof signinIdentifySchema>;

// ----------------------------------------------------------------------
// Password Step
// ----------------------------------------------------------------------

/**
 * Schema for signin password step
 * SECURITY NOTE: Only check for existence (min 1).
 */
export const signinPasswordSchema = z.object({
  password: z
    .string()
    .min(1, SIGNIN_ERROR_MESSAGES.PASSWORD_REQUIRED)
    .max(VALIDATION_RULES.PASSWORD_MAX_LENGTH, SIGNIN_ERROR_MESSAGES.PASSWORD_MAX),
});

export type SigninPasswordFormData = z.infer<typeof signinPasswordSchema>;

// ----------------------------------------------------------------------
// Combined / API Schemas (Merged Flow)
// ----------------------------------------------------------------------

export const signinCredentialsSchema = z.object({
  email: signinIdentifySchema.shape.email,
  password: signinPasswordSchema.shape.password,
});

export type SigninCredentialsFormData = z.infer<typeof signinCredentialsSchema>;

// ----------------------------------------------------------------------
// MFA: TOTP Verification
// ----------------------------------------------------------------------

export const signinTotpSchema = z.object({
  code: z
    .string()
    .min(1, SIGNIN_ERROR_MESSAGES.OTP_REQUIRED)
    .length(VALIDATION_RULES.OTP_LENGTH, SIGNIN_ERROR_MESSAGES.OTP_FORMAT)
    .regex(/^\d+$/, SIGNIN_ERROR_MESSAGES.OTP_FORMAT)
    .trim(),
});

export type SigninTotpFormData = z.infer<typeof signinTotpSchema>;

// ----------------------------------------------------------------------
// MFA: Backup Code Verification
// ----------------------------------------------------------------------

export const signinBackupCodeSchema = z.object({
  code: z
    .string()
    .min(1, SIGNIN_ERROR_MESSAGES.BACKUP_CODE_REQUIRED)
    .min(
      VALIDATION_RULES.BACKUP_CODE_MIN_LENGTH,
      SIGNIN_ERROR_MESSAGES.BACKUP_CODE_MIN
    )
    .trim(),
});

export type SigninBackupCodeFormData = z.infer<typeof signinBackupCodeSchema>;

// ----------------------------------------------------------------------
// NEW: Force Change Password Schema
// ----------------------------------------------------------------------

export const signinForceChangePasswordSchema = z
  .object({
    newPassword: z
      .string()
      .min(1, SIGNIN_ERROR_MESSAGES.PASSWORD_REQUIRED)
      .min(VALIDATION_RULES.PASSWORD_MIN_LENGTH, SIGNIN_ERROR_MESSAGES.PASSWORD_MIN)
      .max(VALIDATION_RULES.PASSWORD_MAX_LENGTH, SIGNIN_ERROR_MESSAGES.PASSWORD_MAX),
    confirmPassword: z.string().min(1, SIGNIN_ERROR_MESSAGES.REQUIRED),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: SIGNIN_ERROR_MESSAGES.PASSWORDS_MUST_MATCH,
    path: ["confirmPassword"],
  });

export type SigninForceChangePasswordFormData = z.infer<
  typeof signinForceChangePasswordSchema
>;

// ----------------------------------------------------------------------
// NEW: MFA Setup Schema (TOTP Confirmation)
// ----------------------------------------------------------------------

export const signinMfaSetupCodeSchema = z.object({
  code: z
    .string()
    .min(1, SIGNIN_ERROR_MESSAGES.OTP_REQUIRED)
    .length(VALIDATION_RULES.OTP_LENGTH, SIGNIN_ERROR_MESSAGES.OTP_FORMAT)
    .regex(/^\d+$/, SIGNIN_ERROR_MESSAGES.OTP_FORMAT)
    .trim(),
});

export type SigninMfaSetupCodeFormData = z.infer<
  typeof signinMfaSetupCodeSchema
>;
